import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CostAllocationRule {
  id: string;
  name: string;
  target_connection_id: string;
  source_connection_id: string;
  cost_types: string[];
  affects_reports: string[];
  operation: 'add' | 'subtract';
  active: boolean;
}

interface CostAllocationConfig {
  enabled: boolean;
  rules: CostAllocationRule[];
}

interface CostAllocationConfigProps {
  organisationId: string;
  connections: any[];
  currentConfig: CostAllocationConfig | null;
  onConfigUpdate: () => void;
  /**
   * FIG-345 (Admin mode): show the configuration without letting it change,
   * e.g. on a merchant's Settings tab while Admin mode is off.
   */
  readOnly?: boolean;
}

const COST_TYPES = [
  'Shipping Services',
  // 'FBA Fees',
  // 'FBA Inventory Fee',
  // 'Service Fee',
  // 'Order Payment',
  // 'Refunds',
];

const REPORT_TYPES = [
  { value: 'fixed-costs', label: 'Fixed Costs' },
  // UC-121 AC-10: "Products Report" -> "Variants Report" at this
  // variant/SKU-grain surface, matching the Reports tab's own label -- UI
  // copy only.
  { value: 'skus', label: 'Variants Report' },
];

export const CostAllocationConfig: React.FC<CostAllocationConfigProps> = ({
  organisationId,
  connections,
  currentConfig,
  onConfigUpdate,
  readOnly = false,
}) => {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(currentConfig?.enabled || false);
  const [rules, setRules] = useState<CostAllocationRule[]>(currentConfig?.rules || []);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    if (readOnly) return;
    try {
      const config: CostAllocationConfig = {
        enabled,
        rules,
      };

      const { error } = await supabase
        .from('organisations')
        .update({ cost_allocation_config: config as any })
        .eq('id', organisationId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Cost allocation configuration saved successfully',
      });
      
      setIsEditing(false);
      onConfigUpdate();
    } catch (error) {
      console.error('Error saving cost allocation config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save cost allocation configuration',
        variant: 'destructive',
      });
    }
  };

  const addRule = () => {
    if (readOnly) return;
    const newRule: CostAllocationRule = {
      id: `rule_${Date.now()}`,
      name: 'New Allocation Rule',
      target_connection_id: '',
      source_connection_id: '',
      cost_types: [],
      affects_reports: ['fixed-costs'],
      operation: 'add',
      active: true,
    };
    setRules([...rules, newRule]);
    setIsEditing(true);
  };

  const updateRule = (index: number, updates: Partial<CostAllocationRule>) => {
    if (readOnly) return;
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], ...updates };
    setRules(newRules);
  };

  const deleteRule = (index: number) => {
    if (readOnly) return;
    setRules(rules.filter((_, i) => i !== index));
  };

  const toggleCostType = (ruleIndex: number, costType: string) => {
    const rule = rules[ruleIndex];
    const newCostTypes = rule.cost_types.includes(costType)
      ? rule.cost_types.filter(t => t !== costType)
      : [...rule.cost_types, costType];
    updateRule(ruleIndex, { cost_types: newCostTypes });
  };

  const toggleReportType = (ruleIndex: number, reportType: string) => {
    const rule = rules[ruleIndex];
    const newReports = rule.affects_reports.includes(reportType)
      ? rule.affects_reports.filter(t => t !== reportType)
      : [...rule.affects_reports, reportType];
    updateRule(ruleIndex, { affects_reports: newReports });
  };

  const getConnectionName = (connectionId: string) => {
    const conn = connections.find(c => c.connectionId === connectionId);
    return conn?.name || connectionId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">Cost Allocation Configuration</h2>
          <p className="text-muted-foreground">
            Allocate costs from one connection to another for split operations
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="enabled">Enabled</Label>
            <Switch
              id="enabled"
              checked={enabled}
              disabled={readOnly}
              onCheckedChange={(checked) => {
                if (readOnly) return;
                setEnabled(checked);
                setIsEditing(true);
              }}
            />
          </div>
          {isEditing && !readOnly && (
            <Button onClick={handleSave}>Save Configuration</Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {rules.map((rule, index) => (
          <Card key={rule.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Input
                    value={rule.name}
                    readOnly={readOnly}
                    onChange={(e) => {
                      updateRule(index, { name: e.target.value });
                      setIsEditing(true);
                    }}
                    className="text-lg font-semibold mb-2"
                    placeholder="Rule name"
                  />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{getConnectionName(rule.source_connection_id) || 'Select source'}</span>
                    <ArrowRight className="h-4 w-4" />
                    <span>{getConnectionName(rule.target_connection_id) || 'Select target'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={rule.active}
                    disabled={readOnly}
                    onCheckedChange={(checked) => {
                      updateRule(index, { active: checked });
                      setIsEditing(true);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={readOnly}
                    aria-label={`Delete rule ${rule.name}`}
                    onClick={() => {
                      deleteRule(index);
                      setIsEditing(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source Connection</Label>
                  <Select
                    value={rule.source_connection_id}
                    disabled={readOnly}
                    onValueChange={(value) => {
                      updateRule(index, { source_connection_id: value });
                      setIsEditing(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((conn) => (
                        <SelectItem key={conn.connectionId} value={conn.connectionId}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Target Connection</Label>
                  <Select
                    value={rule.target_connection_id}
                    disabled={readOnly}
                    onValueChange={(value) => {
                      updateRule(index, { target_connection_id: value });
                      setIsEditing(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select target" />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((conn) => (
                        <SelectItem key={conn.connectionId} value={conn.connectionId}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cost Types to Allocate</Label>
                <div className="flex flex-wrap gap-2">
                  {COST_TYPES.map((costType) => (
                    <Badge
                      key={costType}
                      variant={rule.cost_types.includes(costType) ? 'default' : 'outline'}
                      className={readOnly ? undefined : 'cursor-pointer'}
                      onClick={() => {
                        if (readOnly) return;
                        toggleCostType(index, costType);
                        setIsEditing(true);
                      }}
                    >
                      {costType}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Affects Reports</Label>
                <div className="flex flex-wrap gap-2">
                  {REPORT_TYPES.map((reportType) => (
                    <Badge
                      key={reportType.value}
                      variant={rule.affects_reports.includes(reportType.value) ? 'default' : 'outline'}
                      className={readOnly ? undefined : 'cursor-pointer'}
                      onClick={() => {
                        if (readOnly) return;
                        toggleReportType(index, reportType.value);
                        setIsEditing(true);
                      }}
                    >
                      {reportType.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        <Button onClick={addRule} variant="outline" className="w-full" disabled={readOnly}>
          <Plus className="h-4 w-4 mr-2" />
          Add Allocation Rule
        </Button>
      </div>

      {rules.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No allocation rules configured.</p>
            <p className="text-sm mt-2">
              Add a rule to allocate costs from one connection to another.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
